import TagManager from 'react-gtm-module';
import { initConsentMode } from './consent';

// Environments in which GTM is loaded when the caller doesn't specify its own list.
const DEFAULT_GTM_ENVIRONMENTS = ['develop', 'testing', 'production'];

export interface InitGtmOptions {
  /** GTM container ID, e.g. 'GTM-XXXXXXX'. */
  gtmId: string;
  /** Current app environment, e.g. import.meta.env.VITE_APP_SHOP_ENV. */
  environment: string;
  /** Environments in which GTM should be initialized. Defaults to develop/testing/production. */
  environments?: string[];
}

/**
 * Initialize Google Tag Manager for the current app, but only when running in an
 * allowed environment. Shared across apps so the bootstrap logic lives in one place
 * instead of being duplicated per package.
 */
export function initGtm({
  gtmId,
  environment,
  environments = DEFAULT_GTM_ENVIRONMENTS
}: InitGtmOptions): void {
  if (environments.includes(environment)) {
    // Consent defaults have to reach the dataLayer before the container loads.
    // ES module imports are hoisted, so an app entrypoint cannot order this
    // ahead of its own `vendors/gtm` side-effect import — it belongs here.
    initConsentMode();
    TagManager.initialize({ gtmId });
  }
}

/**
 * Push one event onto the dataLayer, clearing the previous event's `ecommerce`
 * first: GTM merges every push recursively, so without the reset a value from
 * an earlier event leaks into the next one.
 */
export function pushGtmEvent(payload: object): void {
  TagManager.dataLayer({ dataLayer: { ecommerce: null } });
  TagManager.dataLayer({ dataLayer: payload });
}

/** Push data that is not an event, e.g. `user_id`. */
export function pushGtmData(data: object): void {
  TagManager.dataLayer({ dataLayer: data });
}

/**
 * Whether GTM's dataLayer exists. When it does not, `react-gtm-module` falls
 * back to injecting an inline script and JSON-stringifies the payload, which
 * drops any `eventCallback` — so a caller waiting on one would wait forever.
 */
export function hasGtmDataLayer(): boolean {
  return Array.isArray((window as { dataLayer?: unknown }).dataLayer);
}
